import { Link } from "react-router-dom";
import {
  Row,
  Col,
  Card,
  CardHeader,
  CardTitle,
  CardBody,
  Button,
  Badge,
  Alert,
} from "reactstrap";
import { Fragment } from "react";
import * as Icon from "react-feather";
import { PropTypes } from "prop-types";

const CurrentPlan = (props) => {
  const planInfo = props?.planData?.data || {};
  const isFree = planInfo.is_free;
  const isCancelled = Boolean(planInfo.is_cancelled);
  const isExpired = Boolean(planInfo.is_expire);
  const isSuspended = Boolean(planInfo.is_suspended);
  const isInvoiceRenewal = Boolean(planInfo.invoice_renewal);
  const daysRemaining = planInfo.days_until_renewal;
  const expiryDate = planInfo.expiry_date || "N/A";

  return (
    <Fragment>
      {/* ─── Suspended Account Action Banner ─── */}
      {isSuspended && (
        <Alert color="danger" className="mb-2 p-1 p-sm-2 shadow-sm border-danger">
          <div className="d-flex align-items-start">
            <Icon.AlertOctagon size={28} className="text-danger mr-1 flex-shrink-0 mt-25" />
            <div className="flex-grow-1">
              <h5 className="text-danger font-weight-bolder mb-50">
                Account Suspended — Action Required
              </h5>
              <p className="font-small-3 mb-1 text-dark">
                Your account has been suspended following unpaid renewal attempts. Full brand access to dietitian messaging, campaign seeding, and discovery is currently paused.
              </p>
              <div className="p-1 rounded bg-white border border-danger-subtle mb-1">
                <div className="d-flex align-items-center text-dark font-small-3 font-weight-bold">
                  <Icon.CheckCircle size={16} className="text-success mr-50 flex-shrink-0" />
                  <span>Late Payment Protection: Your new subscription period starts on the exact day payment is confirmed today.</span>
                </div>
              </div>
              <div className="d-flex align-items-center flex-wrap">
                {props?.onRequestInvoice && (
                  <Button
                    color="danger"
                    size="sm"
                    className="mr-1 mb-50 font-weight-bold font-small-2"
                    onClick={props.onRequestInvoice}
                  >
                    <Icon.FileText size={14} className="mr-25" /> Pay / Renew by Invoice
                  </Button>
                )}
                <Button
                  tag={Link}
                  to="/subscription-plans"
                  color="outline-danger"
                  size="sm"
                  className="mb-50 font-small-2 font-weight-bold"
                >
                  <Icon.CreditCard size={14} className="mr-25" /> Pay by Card
                </Button>
              </div>
            </div>
          </div>
        </Alert>
      )}

      <Card className="card-user-timeline shadow-sm">
        <CardHeader className="d-flex justify-content-between align-items-center border-bottom pb-1">
          <CardTitle tag="h4" className="mb-0">
            Current Subscription Plan
          </CardTitle>
          <div>
            {isSuspended ? (
              <Badge color="light-danger" pill className="font-small-2">
                <Icon.AlertOctagon size={12} className="mr-25" /> Account Suspended
              </Badge>
            ) : isExpired ? (
              <Badge color="light-danger" pill className="font-small-2">
                <Icon.AlertCircle size={12} className="mr-25" /> Expired
              </Badge>
            ) : isCancelled ? (
              <Badge color="light-warning" pill className="font-small-2">
                <Icon.AlertTriangle size={12} className="mr-25" /> Cancelled (Non-Renewing)
              </Badge>
            ) : isInvoiceRenewal ? (
              <Badge color="light-info" pill className="font-small-2">
                <Icon.FileText size={12} className="mr-25" /> Pay by Invoice at Expiry
              </Badge>
            ) : (
              <Badge color="light-success" pill className="font-small-2">
                <Icon.CheckCircle size={12} className="mr-25" /> Active (Auto-Renewing Card)
              </Badge>
            )}
          </div>
        </CardHeader>

        <div className="bg-light p-1 p-sm-2 border-bottom">
          <Row className="align-items-center">
            <Col sm="7" className="mb-1 mb-sm-0">
              {isFree && (
                <h4 className="font-weight-bolder text-primary mb-50">Free Plan</h4>
              )}

              {!isFree && !isExpired && (
                <div>
                  <h4 className="font-weight-bolder text-dark mb-25">
                    {planInfo.plan || "Brand Plan"}
                  </h4>
                  <div className="d-flex align-items-baseline">
                    <h3 className="text-primary font-weight-bolder mb-0 mr-50">
                      {planInfo.price || "N/A"}
                    </h3>
                    <span className="text-muted font-small-3">
                      / {planInfo.plan_short_title || "Year"}
                    </span>
                    {planInfo.subscription_type && (
                      <Badge
                        color="light-primary"
                        pill
                        className="ml-1 text-capitalize font-small-2"
                      >
                        {planInfo.subscription_type}
                      </Badge>
                    )}
                  </div>
                </div>
              )}

              {isExpired && (
                <div>
                  <h4 className="font-weight-bolder text-danger mb-25">
                    No Active Subscription
                  </h4>
                  <p className="text-muted font-small-3 mb-0">
                    Your previous subscription has expired. Purchase or renew a plan to restore access.
                  </p>
                </div>
              )}
            </Col>

            <Col sm="5" className="text-sm-right">
              {isExpired ? (
                <div className="d-flex justify-content-sm-end flex-wrap">
                  <Button
                    tag={Link}
                    to="/subscription-plans"
                    color="primary"
                    className="d-inline-flex align-items-center mr-50 mb-50"
                  >
                    <Icon.ShoppingCart size={14} className="mr-50" />
                    <span>Purchase Plan</span>
                  </Button>
                  {props?.onRequestInvoice && (
                    <Button
                      color="outline-primary"
                      className="d-inline-flex align-items-center mb-50"
                      onClick={props.onRequestInvoice}
                    >
                      <Icon.FileText size={14} className="mr-50" />
                      <span>Pay by Invoice</span>
                    </Button>
                  )}
                </div>
              ) : (
                <div className="d-inline-block text-left bg-white p-75 rounded border">
                  <div className="d-flex align-items-center mb-25">
                    <Icon.Calendar size={14} className="text-primary mr-50" />
                    <span className="font-small-2 text-muted font-weight-bold">
                      {isCancelled
                        ? "Access Ends:"
                        : isInvoiceRenewal
                        ? "Invoice Due Date:"
                        : "Next Renewal Date:"}
                    </span>
                  </div>
                  <div className="font-weight-bolder text-dark font-small-3 ml-2">
                    {expiryDate || "N/A"}
                  </div>
                  {daysRemaining !== undefined && daysRemaining !== null && (
                    <div className="ml-2 mt-25">
                      <Badge
                        color={
                          daysRemaining <= 5
                            ? "light-danger"
                            : daysRemaining <= 30
                            ? "light-warning"
                            : "light-info"
                        }
                        pill
                        className="font-small-1"
                      >
                        {daysRemaining > 0
                          ? `${daysRemaining} days remaining`
                          : "Ending today"}
                      </Badge>
                    </div>
                  )}
                </div>
              )}
            </Col>
          </Row>
        </div>

        <CardBody className="py-1">
          {!isExpired && (
            <div className="mb-1">
              <h6 className="font-weight-bold font-small-3 mb-50">
                Billing & Renewal Preferences:
              </h6>

              {isCancelled ? (
                <div>
                  <p className="text-warning font-small-3 mb-50">
                    <Icon.AlertTriangle size={14} className="mr-50" />
                    Auto-renewal has been cancelled. Your brand access will remain active until{" "}
                    <strong>{expiryDate}</strong>. After this date, your subscription will expire.
                  </p>
                  <div className="d-flex align-items-center flex-wrap pt-25">
                    {props?.onReactivate && (
                      <Button
                        color="success"
                        size="sm"
                        className="mr-50 mb-25 font-small-2 font-weight-bold"
                        onClick={props.onReactivate}
                        disabled={props.isReactivating}
                      >
                        <Icon.CheckCircle size={12} className="mr-25" /> Reactivate Auto-Renewal
                      </Button>
                    )}
                    {props?.onRequestInvoice && (
                      <Button
                        color="outline-primary"
                        size="sm"
                        className="mb-25 font-small-2"
                        onClick={props.onRequestInvoice}
                      >
                        <Icon.FileText size={12} className="mr-25" /> Renew by Invoice
                      </Button>
                    )}
                  </div>
                </div>
              ) : isInvoiceRenewal ? (
                /* ─── Mode: Pay by Invoice at End of Subscription ─── */
                <div className="p-1 rounded bg-light border mb-1">
                  <div className="d-flex align-items-start justify-content-between flex-wrap">
                    <div className="mr-1 mb-50">
                      <div className="d-flex align-items-center mb-25">
                        <Icon.FileText size={16} className="text-primary mr-50" />
                        <strong className="text-dark font-small-3">
                          Pay by Invoice at End of Subscription (Active)
                        </strong>
                      </div>
                      <p className="text-muted font-small-2 mb-0">
                        When your subscription cycle reaches <strong>{expiryDate}</strong>, an official Stripe invoice with Net 30 terms will be automatically generated and emailed to your billing contact. Your card will <strong>not</strong> be automatically charged.
                      </p>
                    </div>

                    <div className="d-flex align-items-center flex-wrap pt-25">
                      {props?.onToggleInvoiceRenewal && (
                        <Button
                          color="outline-secondary"
                          size="sm"
                          className="mr-50 mb-25 font-small-2"
                          onClick={() => props.onToggleInvoiceRenewal(false)}
                        >
                          <Icon.RefreshCw size={12} className="mr-25" /> Switch to Auto-Renew Card
                        </Button>
                      )}
                      {props?.onRequestInvoice && (
                        <Button
                          color="primary"
                          size="sm"
                          className="mb-25 font-small-2 font-weight-bold"
                          onClick={props.onRequestInvoice}
                        >
                          <Icon.FileText size={12} className="mr-25" /> Request / View Invoice
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                /* ─── Mode: Auto-Renew via Credit Card (with 3 retries) ─── */
                <div className="p-1 rounded bg-light border mb-1">
                  <div className="d-flex align-items-start justify-content-between flex-wrap">
                    <div className="mr-1 mb-50">
                      <div className="d-flex align-items-center mb-25">
                        <Icon.RefreshCw size={16} className="text-success mr-50" />
                        <strong className="text-dark font-small-3">
                          Auto-Renew via Card (Active)
                        </strong>
                      </div>
                      <p className="text-muted font-small-2 mb-0">
                        Your subscription will automatically renew and charge your card on file on{" "}
                        <strong className="text-dark">{expiryDate || "N/A"}</strong>. In case of payment failure, up to 3 automatic retries spaced 5 days apart will protect your account before suspension.
                      </p>
                    </div>

                    <div className="d-flex align-items-center flex-wrap pt-25">
                      {props?.onToggleInvoiceRenewal && (
                        <Button
                          color="outline-primary"
                          size="sm"
                          className="mr-50 mb-25 font-small-2"
                          onClick={() => props.onToggleInvoiceRenewal(true)}
                        >
                          <Icon.FileText size={12} className="mr-25" /> Pay by Invoice at End of Subscription
                        </Button>
                      )}
                      <button
                        className="btn btn-link text-danger p-0 font-small-2 mb-25"
                        onClick={props?.openingModal}
                      >
                        Cancel subscription
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {!isExpired && planInfo.plan_features && planInfo.plan_features.length > 0 && (
            <div>
              <h6 className="font-weight-bold font-small-3 mb-50">Included Features:</h6>
              <Row>
                {planInfo.plan_features.map((item, idx) => (
                  <Col md="6" key={idx} className="mb-25">
                    <div className="d-flex align-items-center font-small-3 text-secondary">
                      <Icon.CheckCircle size={14} className="mr-50 text-success" />
                      <span>{item.title}</span>
                    </div>
                  </Col>
                ))}
              </Row>
            </div>
          )}
        </CardBody>
      </Card>
    </Fragment>
  );
};

CurrentPlan.propTypes = {
  planData: PropTypes.object,
  openingModal: PropTypes.func.isRequired,
  onUpgradeClick: PropTypes.func,
  onReactivate: PropTypes.func,
  onRequestInvoice: PropTypes.func,
  onToggleInvoiceRenewal: PropTypes.func,
  isReactivating: PropTypes.bool,
};

export default CurrentPlan;
